/**
 * Personal scheduled automations within the selected organisation.
 *
 * Same idiom as `state/policies.ts`: a per-org cached atom over the workspace
 * server's `/api/amoreg/automations`, which forwards to the domain API where
 * the database work and the tenant guard live. Nothing here decides who may
 * see what — the API restricts the list and every change to the signed-in
 * owner and checks their organisation membership.
 *
 * `nextRunAt` is never sent: the API computes it from the schedule, so a
 * client cannot make an automation fire immediately and repeatedly. The page
 * does read the schedule — through the shared `@repo/shared/automationSchedule`
 * module the API itself uses — to describe, validate and preview a draft
 * before it is saved; for a stored automation the API's `nextRunAt`,
 * `nextRunLabel` and `scheduleLabel` remain the authority.
 *
 * Fields newer than the oldest API this page may meet (`action`, `finished`,
 * `nextRunLabel`) decode with defaults, so the web and the API can deploy in
 * either order.
 *
 * @module state/automations
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { reasonFromBody, type OrgProfileUnavailableReason } from "./orgProfile";

/**
 * The schedule as stored — the shared model's shape, so a decoded schedule
 * goes straight into `normalizeSchedule` / `computeNextRunAt` / `describeSchedule`.
 * Keys are `optionalKey` and arrays mutable to match it exactly.
 */
export type AutomationSchedule = typeof ScheduleSchema.Type;

const ScheduleSchema = Schema.Struct({
  frequency: Schema.Literals(["once", "interval", "daily", "weekdays", "weekly", "monthly"]),
  time: Schema.String,
  timezone: Schema.String,
  date: Schema.optionalKey(Schema.String),
  everyMinutes: Schema.optionalKey(Schema.Number),
  until: Schema.optionalKey(Schema.String),
  weekdays: Schema.optionalKey(Schema.mutable(Schema.Array(Schema.Number))),
  /** Legacy weekly field — one day; read when `weekdays` is absent. */
  weekday: Schema.optionalKey(Schema.Number),
  dayOfMonth: Schema.optionalKey(Schema.Number),
  startDate: Schema.optionalKey(Schema.String),
  endDate: Schema.optionalKey(Schema.String),
});

/**
 * A connected-app call the owner approved in chat ("send this email at
 * 15:44"). Read-only here: the browser can pause, reschedule or delete it,
 * never mint or change one. `kind` and `accountScope` decode as plain strings
 * so a new kind cannot blank the whole list.
 */
const ActionSchema = Schema.Struct({
  kind: Schema.String,
  accountScope: Schema.String,
  connectorId: Schema.String,
  toolSlug: Schema.String,
  version: Schema.String,
  arguments: Schema.Record(Schema.String, Schema.Unknown),
  threadId: Schema.NullOr(Schema.String),
  attachments: Schema.Array(Schema.Struct({ path: Schema.String, sha256: Schema.String })),
  /** "Gmail: send email to sam@example.com — Board pack". */
  summary: Schema.String,
  approvedAt: Schema.String,
});
export type AutomationAction = typeof ActionSchema.Type;

const AutomationSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  prompt: Schema.String,
  schedule: ScheduleSchema,
  modelId: Schema.String,
  allowedTools: Schema.NullOr(Schema.Array(Schema.String)),
  skillIds: Schema.Array(Schema.String),
  jurisdictions: Schema.NullOr(Schema.Array(Schema.String)),
  /** The approved send it carries out; null for a prompt automation. */
  action: Schema.NullOr(ActionSchema).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  enabled: Schema.Boolean,
  /**
   * Switched off with nothing left to run — a one-off that ran, or a rule past
   * its end date. Resuming it needs a new schedule.
   */
  finished: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  lastRunAt: Schema.NullOr(Schema.String),
  nextRunAt: Schema.String,
  /** "Fri 25 Sep 2026, 15:44 (Europe/London)"; empty from an older API. */
  nextRunLabel: Schema.String.pipe(Schema.withDecodingDefault(Effect.succeed(""))),
  /** "Weekly on Monday at 9:00 AM", rendered by the API. */
  scheduleLabel: Schema.String,
});
export type AutomationView = typeof AutomationSchema.Type;

const AutomationListSchema = Schema.Struct({
  automations: Schema.Array(AutomationSchema),
});
export type AutomationListSnapshot = typeof AutomationListSchema.Type;

const decodeAutomations = Schema.decodeUnknownEffect(AutomationListSchema);

const automationsUrl = (organizationId: string, extra?: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/automations", { organizationId, ...extra });

const fetchAutomations = (organizationId: string) =>
  Effect.gen(function* () {
    const response = yield* Effect.tryPromise(() =>
      fetch(automationsUrl(organizationId), { credentials: "include" }),
    );
    if (!response.ok) {
      const body = yield* Effect.promise(() => response.text().catch(() => ""));
      return { unavailable: reasonFromBody(body) } as const;
    }
    const json = yield* Effect.tryPromise(() => response.json());
    return yield* decodeAutomations(json);
  }).pipe(Effect.orElseSucceed(() => ({ unavailable: "error" as const })));

const automationsRuntime = Atom.runtime(Layer.empty);

const automationsAtom = Atom.family((organizationId: string) =>
  automationsRuntime
    .atom(organizationId.length === 0 ? Effect.succeed(null) : fetchAutomations(organizationId))
    .pipe(Atom.withLabel(`web-amoreg:automations:${organizationId}`)),
);

/**
 * Everything the panel can set. Absent keys keep their stored value: creating
 * needs all three of title, prompt and schedule; an edit sends only what
 * changed — a scheduled send's title and prompt are fixed, and an unchanged
 * schedule is left out so saving a title does not reschedule the next run.
 * `enabled: true` beside a new schedule turns a finished automation back on.
 */
export interface AutomationInput {
  readonly title?: string;
  readonly prompt?: string;
  readonly schedule?: AutomationSchedule;
  readonly enabled?: boolean;
  readonly skillIds?: ReadonlyArray<string>;
}

export interface AutomationsView {
  readonly automations: ReadonlyArray<AutomationView>;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly isPending: boolean;
  readonly refresh: () => void;
  /** Each mutation resolves to an error message, or null on success. */
  readonly save: (input: AutomationInput, automationId?: string) => Promise<string | null>;
  readonly setEnabled: (automationId: string, enabled: boolean) => Promise<string | null>;
  readonly remove: (automationId: string) => Promise<string | null>;
}

const messageFrom = (body: string, fallback: string): string => {
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    return typeof parsed.message === "string" && parsed.message.length > 0
      ? parsed.message
      : fallback;
  } catch {
    return fallback;
  }
};

export function useAutomations(organizationId: string | null): AutomationsView {
  const atom = useMemo(() => automationsAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);

  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailableReason =
    data !== null && typeof data === "object" && "unavailable" in data
      ? (data.unavailable as OrgProfileUnavailableReason)
      : result._tag === "Failure"
        ? ("error" as const)
        : null;
  const snapshot =
    unavailableReason === null ? ((data ?? null) as AutomationListSnapshot | null) : null;

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const write = useCallback(
    async (url: string, init: RequestInit, fallback: string): Promise<string | null> => {
      try {
        const response = await fetch(url, { credentials: "include", ...init });
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return messageFrom(body, fallback);
        }
        refresh();
        return null;
      } catch {
        return fallback;
      }
    },
    [refresh],
  );

  const save = useCallback(
    (input: AutomationInput, automationId?: string) => {
      if (!organizationId) return Promise.resolve("No organization selected.");
      return write(
        automationsUrl(organizationId, automationId ? { automationId } : undefined),
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.prompt !== undefined ? { prompt: input.prompt } : {}),
            ...(input.schedule !== undefined ? { schedule: input.schedule } : {}),
            ...(input.enabled !== undefined ? { enabled: input.enabled } : {}),
            ...(input.skillIds ? { skillIds: [...input.skillIds] } : {}),
          }),
        },
        automationId
          ? "The automation could not be saved."
          : "The automation could not be created.",
      );
    },
    [organizationId, write],
  );

  const setEnabled = useCallback(
    (automationId: string, enabled: boolean) => {
      if (!organizationId) return Promise.resolve("No organization selected.");
      // Only `enabled` rides along: sending the schedule too would recompute
      // the next run, so pausing and resuming would quietly reschedule it.
      return write(
        automationsUrl(organizationId, { automationId }),
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled }),
        },
        enabled ? "The automation could not be resumed." : "The automation could not be paused.",
      );
    },
    [organizationId, write],
  );

  const remove = useCallback(
    (automationId: string) => {
      if (!organizationId) return Promise.resolve("No organization selected.");
      return write(
        automationsUrl(organizationId, { automationId }),
        { method: "DELETE" },
        "The automation could not be deleted.",
      );
    },
    [organizationId, write],
  );

  return {
    automations: snapshot?.automations ?? [],
    unavailableReason,
    isPending: result.waiting,
    refresh,
    save,
    setEnabled,
    remove,
  };
}
